import React from 'react';
import { DollarSign, Wallet, TrendingUp, Percent } from 'lucide-react';

/**
 * Компонент секции финансовых параметров
 */
const FinancialParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Wallet className="w-5 h-5 mr-2 text-indigo-500" />
        Финансовые параметры
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Текущие накопления */}
        <div>
          <label htmlFor="currentSavings" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Текущие накопления (₽)
          </label>
          <input
            type="text"
            id="currentSavings"
            name="currentSavings"
            value={formData.currentSavings}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.currentSavings ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.currentSavings && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.currentSavings}
            </p>
          )}
        </div>

        {/* Ежемесячный доход */}
        <div>
          <label htmlFor="monthlyIncome" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Ежемесячный доход (₽)
          </label>
          <input
            type="text"
            id="monthlyIncome"
            name="monthlyIncome"
            value={formData.monthlyIncome}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.monthlyIncome ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.monthlyIncome && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.monthlyIncome}
            </p>
          )}
        </div>

        {/* Ежемесячные расходы */}
        <div>
          <label htmlFor="monthlyExpenses" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Ежемесячные расходы (₽)
          </label>
          <input
            type="text"
            id="monthlyExpenses"
            name="monthlyExpenses"
            value={formData.monthlyExpenses}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.monthlyExpenses ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.monthlyExpenses && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.monthlyExpenses}
            </p>
          )}
        </div>

        {/* Ежемесячные пополнения */}
        <div>
          <label htmlFor="monthlyAdditions" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Ежемесячные пополнения (₽)
          </label>
          <input
            type="text"
            id="monthlyAdditions"
            name="monthlyAdditions"
            value={formData.monthlyAdditions}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.monthlyAdditions ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.monthlyAdditions && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.monthlyAdditions}
            </p>
          )}
        </div>

        {/* Процент сбережений */}
        <div>
          <label htmlFor="savingsRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Percent className="w-4 h-4 mr-2 text-indigo-500" />
            Процент сбережений (%)
          </label>
          <input
            type="text"
            id="savingsRate"
            name="savingsRate"
            value={formData.savingsRate}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.savingsRate ? 'input-error' : ''}`}
            placeholder="Введите процент"
          />
          {errors.savingsRate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.savingsRate}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Какой процент дохода вы откладываете ежемесячно
          </p>
        </div>

        {/* Желаемый пассивный доход */}
        <div>
          <label htmlFor="desiredPassiveIncome" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <DollarSign className="w-4 h-4 mr-2 text-indigo-500" />
            Желаемый пассивный доход (₽/мес)
          </label>
          <input
            type="text"
            id="desiredPassiveIncome"
            name="desiredPassiveIncome"
            value={formData.desiredPassiveIncome}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.desiredPassiveIncome ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.desiredPassiveIncome && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.desiredPassiveIncome}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Сумма, которую вы хотите получать ежемесячно после выхода на пенсию
          </p>
        </div>

        {/* Ожидаемая инфляция */}
        <div>
          <label htmlFor="expectedInflation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2 text-indigo-500" />
            Ожидаемая инфляция (%)
          </label>
          <input
            type="text"
            id="expectedInflation"
            name="expectedInflation"
            value={formData.expectedInflation}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.expectedInflation ? 'input-error' : ''}`}
            placeholder="Введите процент"
          />
          {errors.expectedInflation && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.expectedInflation}
            </p>
          )}
        </div>

        {/* Учитывать инфляцию */}
        <div className="col-span-full">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="useInflationAdjusted"
              checked={formData.useInflationAdjusted}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать влияние инфляции при расчетах
            </span>
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
            Если включено, расчеты будут учитывать снижение покупательной способности денег
          </p>
        </div>
      </div>
    </div>
  );
};

export default FinancialParametersSection;